import validateCategoryParameters from './validateCategoryParameters';
import { default as itemsCategoriesStoreUpdate } from 'owa-mail-actions/lib/triage/itemsCategoriesStoreUpdate';
import { getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import { ActionSource, selectMailStoreItemById } from 'owa-mail-store';
import * as undoActions from 'owa-mail-undo';
import tombstoneOperations, { TombstoneReasonType } from 'owa-mail-list-tombstone';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import {
    onTriageActionCompleted,
    onTriageActionFailed,
} from 'owa-mail-triage-action-response-processor';
import { getApolloClient } from 'owa-apollo';
import { CategorizeItemSnapshot } from 'owa-graph-schema';
import {
    CategorizeItemDocument,
    CategorizeItemMutation,
} from '../../../graphql/__generated__/categorizeItemMutation.interface';
import { MailboxInfoInput } from 'owa-graph-schema';

type CategorizeItemResult = CategorizeItemMutation['categorizeItem'];

/**
 * Internal method to update categories
 * @param itemRowKeys the row keys of the items
 * @param tableView that contains the items
 * @param categoriesToAdd the categories to be added to the items
 * @param categoriesToRemove the categories to be removed from the items
 * @param clearCategories whether to clear categories
 * @param actionSource where this action is triggered from
 * @param promiseToWaitUpon promise to wait upon before sending the update category request.
 */
export default function updateItemsCategoriesInternal(
    itemRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource,
    promiseToWaitUpon: Promise<boolean>
): Promise<void> {
    // Validate category parameters and throw error if they are invalid
    validateCategoryParameters(itemRowKeys, categoriesToAdd, categoriesToRemove, clearCategories);
    undoActions.clearLastUndoableAction();

    if (promiseToWaitUpon) {
        // In case where user creates a category from the commanding bar drop down
        // we want to only send the request to apply the category only if the creation of
        // category succeeds. Today the promiseToWaitUpon here is create Category action
        return promiseToWaitUpon.then(isPromiseSuccessful => {
            return submitRequestAndMakeLocalUpdate(
                itemRowKeys,
                tableView,
                categoriesToAdd,
                categoriesToRemove,
                clearCategories
            );
        });
    } else {
        return submitRequestAndMakeLocalUpdate(
            itemRowKeys,
            tableView,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories
        );
    }
}

function submitRequestAndMakeLocalUpdate(
    itemRowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean
) {
    const itemIdsToUpdate = getRowIdsFromRowKeys(itemRowKeys, tableView.id);

    // 1. Update the items categories in store.
    // After items categories got updated we can submit changes to the server.
    itemsCategoriesStoreUpdate(
        itemIdsToUpdate,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    // 2. Add category operation to tombstone
    // The local update operation should happen before adding category to tombstone
    // else we may not have correct set of categories when adding it to the tombstone
    tombstoneOperations.add(itemRowKeys, tableView, TombstoneReasonType.Category);

    // 3. submit server request
    const itemContexts = createCategorizeItemSnapshots(itemIdsToUpdate);
    return submitUpdateItemCategories(itemRowKeys, tableView, itemContexts);
}

async function submitUpdateItemCategories(
    itemRowKeys: string[],
    tableView: TableView,
    itemContexts: CategorizeItemSnapshot[]
): Promise<void> {
    const mailboxInfo = getMailboxInfo(tableView);

    try {
        await exportedHelperFunctions.invokeCategorizeItemMutation(itemContexts, mailboxInfo);
        onTriageActionCompleted(itemRowKeys, tableView.serverFolderId, true);
    } catch (error) {
        onTriageActionCompleted(itemRowKeys, tableView.serverFolderId, true);
        onTriageActionFailed(tableView);
        return Promise.reject(error);
    }
}

/**
 * Create item snapshots for the mutation
 * @param itemIds to be updated
 * @returns an array of CategorizeItemSnapshot which is used for the mutation
 */
function createCategorizeItemSnapshots(itemIdsToUpdate: string[]): CategorizeItemSnapshot[] {
    const itemContexts: CategorizeItemSnapshot[] = [];
    itemIdsToUpdate.forEach(itemId => {
        const item = selectMailStoreItemById(itemId);
        itemContexts.push({
            id: item.ItemId.Id,
            categories: item.Categories,
        });
    });

    return itemContexts;
}

const invokeCategorizeItemMutationFn = async function invokeCategorizeItemMutation(
    itemContexts: CategorizeItemSnapshot[],
    mailboxInfo: MailboxInfoInput
): Promise<CategorizeItemResult> {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            items: itemContexts,
            mailboxInfo: mailboxInfo,
        },
        mutation: CategorizeItemDocument,
    });

    return result?.data?.categorizeItem;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeCategorizeItemMutation: invokeCategorizeItemMutationFn,
};
